"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import { transport, useTransportMutation } from "@/transport"
import {
  Room,
  RoomEvent,
  Track,
  type RemoteAudioTrack,
  type RemoteTrackPublication,
} from "livekit-client"

import { mergeTranscriptEvent } from "../utils/mergeTranscriptEvent"
import { mergeTranscriptSegment } from "../utils/mergeTranscriptSegment"
import {
  ANTI_CLICK_FADE_SECONDS,
  attachSpeakerMeter,
  fadeGainsForTeardown,
  rampGainToMute,
} from "./attachSpeakerMeter"
import {
  getDisconnectReasonMessage,
  getSimulationRoomErrorMessage,
} from "./simulationRoomErrorMessages"
import type {
  Envelope,
  SimulationRoom,
  SimulationRoomEvent,
  SimulationRoomOptions,
  SimulationRoomSegment,
  SimulationRoomStatus,
} from "./simulationRoomTypes"
import { useSimulationRoomTimer } from "./useSimulationRoomTimer"
import { useUnloadAudioFade } from "./useUnloadAudioFade"

export function useSimulationRoom(
  simulationId: string,
  { isLive = true, startMuted = false }: SimulationRoomOptions = {},
): SimulationRoom {
  const [status, setStatus] = useState<SimulationRoomStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const [levels, setLevels] = useState<Record<string, number>>({})
  const [segments, setSegments] = useState<Map<string, SimulationRoomSegment>>(
    new Map(),
  )
  const [events, setEvents] = useState<Map<string, SimulationRoomEvent>>(
    new Map(),
  )
  const [isMuted, setIsMuted] = useState(startMuted)
  const {
    elapsedSeconds,
    anchorTimer,
    resetElapsed,
    timerAnchorRef,
    latestSessionRef,
  } = useSimulationRoomTimer(status)

  // How long a listener waits for the simulation to join before being told that
  // it is not coming. The worker polls for the room every couple of seconds, so
  // anything beyond a few of those means it is not watching — most often because
  // the simulation is running somewhere without simulation rooms configured.
  const JOIN_TIMEOUT_MS = 15_000

  const roomRef = useRef<Room | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const elementsRef = useRef<HTMLAudioElement[]>([])
  // Keyed by track name (AGENT/PERSONA) rather than a single handle: each
  // subscribed speaker runs its own self-rescheduling meter loop, and a
  // shared ref would let the second one silently orphan the first — teardown
  // would only ever cancel whichever loop wrote here last.
  const frameRef = useRef<Record<string, number>>({})
  const sessionSeqRef = useRef(0)
  // Each speaker's own running peak, so the meters read as comparable activity
  // regardless of how differently loud the two sources were actually captured
  // (telephony-narrowband agent audio vs. synthesized TTS persona audio, say).
  // A fixed multiplier common to both would instead just show whichever one
  // happens to be louder as maxed out and the other as barely moving.
  const peaksRef = useRef<Record<string, number>>({})
  // Bumped by every start() and stop(), so a room-request callback that
  // resolves after the user has since stopped (or started again) can tell
  // it is stale — the transport mutation itself cannot be cancelled once
  // in flight.
  const requestGenerationRef = useRef(0)
  // Mirrors `status` for the Disconnected handler, which runs on LiveKit's
  // own event timing and needs the true latest status rather than whatever
  // was captured in the closure when start() was called.
  const latestStatusRef = useRef<SimulationRoomStatus>("idle")
  // TrackSubscribed closures are created once per start() and read this
  // rather than `isMuted` directly, so a mute toggled mid-session applies to
  // speakers who join afterward instead of only the ones already attached.
  // Written synchronously by toggleMute — a passive-effect sync would leave
  // a window between the click and the effect flush where a speaker
  // subscribing came up audible while the dock already showed muted.
  const isMutedRef = useRef(startMuted)
  // The GainNode actually driving each speaker's audible output (see
  // `attachSpeakerMeter`) — toggleMute ramps these directly rather than
  // touching the (permanently muted) elements in `elementsRef`.
  const gainNodesRef = useRef<GainNode[]>([])
  // Set while stop() is fading gains to 0 before it tears the room down —
  // see stop() and the flush at the top of start().
  const stopFadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const requestRoom = useTransportMutation(
    transport.operations.requestSimulationRoom,
  )

  useEffect(() => {
    latestStatusRef.current = status
  }, [status])

  const toggleMute = useCallback(() => {
    const next = !isMutedRef.current
    isMutedRef.current = next
    // Create the context here if no track has yet (unmuting before the
    // simulation publishes audio): a context born inside this gesture starts
    // running, so speakers that attach later come up audible — one created
    // later by a track subscription would sit suspended with no gesture left
    // to resume it.
    const context = (audioContextRef.current ??= new AudioContext())
    const applyRamps = () => {
      for (const gain of gainNodesRef.current) {
        rampGainToMute(context, gain, next, ANTI_CLICK_FADE_SECONDS)
      }
    }
    if (!next && context.state === "suspended") {
      // Unmuting is the gesture that resumes a startMuted session's context —
      // and the ramp must wait for the clock to run: scheduled while
      // suspended, it has already "elapsed" on resume, stepping the gain to
      // full mid-wave (a loud pop) instead of fading.
      void context.resume().then(applyRamps, applyRamps)
    } else {
      applyRamps()
    }
    setIsMuted(next)
  }, [])

  // A simulation that has not started has nothing watching for a listener yet,
  // so the clock on "it should have joined by now" starts when it goes live.
  useEffect(() => {
    if (status !== "waiting") {
      return
    }
    if (!isLive) {
      // The simulation ended before its worker ever joined this room —
      // nobody is coming, so there is nothing left to count down to.
      // Without this, a room that goes not-live while still waiting would
      // just have its timeout cleared and never replaced, leaving the panel
      // stuck showing "waiting" forever.
      setStatus((current) => (current === "waiting" ? "stalled" : current))
      return
    }
    const handle = setTimeout(() => {
      setStatus((current) => (current === "waiting" ? "stalled" : current))
    }, JOIN_TIMEOUT_MS)
    return () => clearTimeout(handle)
  }, [isLive, status])

  const teardown = useCallback(() => {
    // A stop()-scheduled fade may still be pending (see stop()) — this
    // teardown supersedes it, whether it got here via that timeout firing or
    // via start()/unmount racing ahead of it.
    if (stopFadeTimeoutRef.current !== null) {
      clearTimeout(stopFadeTimeoutRef.current)
      stopFadeTimeoutRef.current = null
    }
    for (const frameId of Object.values(frameRef.current)) {
      cancelAnimationFrame(frameId)
    }
    frameRef.current = {}
    for (const element of elementsRef.current) {
      element.srcObject = null
      element.remove()
    }
    elementsRef.current = []
    gainNodesRef.current = []
    void audioContextRef.current?.close()
    audioContextRef.current = null
    void roomRef.current?.disconnect()
    roomRef.current = null
    sessionSeqRef.current = 0
    timerAnchorRef.current = null
    latestSessionRef.current = null
    peaksRef.current = {}
  }, [latestSessionRef, timerAnchorRef])

  useEffect(
    () => () => {
      // Bumping this on unmount, same as stop(), means a start() attempt
      // still in flight (mutation pending, or awaiting room.connect()) is
      // stale by the time it resolves — the existing generation checks in
      // start() then disconnect the local room it just stood up instead of
      // assigning it to a ref nothing mounted can ever call stop() on.
      requestGenerationRef.current += 1
      // Same anti-pop fade as stop(). These refs belong to this instance, so
      // the delayed teardown cannot touch a remounted session's resources.
      if (fadeGainsForTeardown(audioContextRef.current, gainNodesRef.current)) {
        setTimeout(teardown, ANTI_CLICK_FADE_SECONDS * 1000)
      } else {
        teardown()
      }
    },
    [teardown],
  )

  useUnloadAudioFade(audioContextRef, gainNodesRef)

  const applyMessage = useCallback(
    (envelope: Envelope) => {
      if (
        envelope.type === "transcript.segment" ||
        envelope.type === "transcript.event"
      ) {
        if (envelope.type === "transcript.segment") {
          setSegments((current) => mergeTranscriptSegment(current, envelope))
        } else {
          setEvents((current) => mergeTranscriptEvent(current, envelope))
        }
        // Either kind is real call content: anchor the timer and go live.
        const session = latestSessionRef.current
        if (session && timerAnchorRef.current === null) {
          anchorTimer(session.startedAt, session.serverNow)
        }
        setStatus((current) =>
          current === "waiting" ||
          current === "connected" ||
          current === "stalled"
            ? "live"
            : current,
        )
        return
      }

      if (envelope.type === "session") {
        if (envelope.seq <= sessionSeqRef.current) {
          return
        }
        sessionSeqRef.current = envelope.seq
        const payload = envelope.payload as {
          started_at: number
          server_now: number
          status: string
        }
        latestSessionRef.current = {
          startedAt: payload.started_at,
          serverNow: payload.server_now,
        }
        if (payload.status === "complete" || payload.status === "failed") {
          setStatus("complete")
        } else if (envelope.seq > 1) {
          // The first session message announces the room. The later running
          // message marks the first real audio/transcript activity. It may
          // arrive live before the older replay, so use the sequence number
          // instead of counting messages received by this listener.
          anchorTimer(payload.started_at, payload.server_now)
          setStatus("live")
        } else {
          setStatus("connected")
        }
      }
    },
    [anchorTimer, latestSessionRef, timerAnchorRef],
  )

  const meter = useCallback((track: RemoteAudioTrack, name: string) => {
    const gain = attachSpeakerMeter(
      track,
      name,
      { audioContextRef, peaksRef, frameRef, setLevels },
      isMutedRef.current,
    )
    gainNodesRef.current.push(gain)
  }, [])

  const start = useCallback(() => {
    if (
      status === "connecting" ||
      status === "live" ||
      status === "waiting" ||
      status === "connected"
    ) {
      return
    }
    if (stopFadeTimeoutRef.current !== null) {
      // A previous stop() is still fading its gains to 0 (see stop()) —
      // finish that teardown now rather than letting the delayed teardown()
      // fire under the fresh session this call is about to stand up.
      teardown()
    }
    setStatus("connecting")
    setError(null)
    // The transport mutation can't be cancelled once in flight, so a stop()
    // (or a fresh start()) that happens before this callback resolves has to
    // be detected after the fact instead — this generation is that check.
    const generation = ++requestGenerationRef.current

    requestRoom.mutate(
      { simulation_id: simulationId },
      {
        // The room connection must await LiveKit, while the mutation callback
        // API intentionally does not consume a returned promise.
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- LiveKit setup must await connection inside this callback
        onSuccess: async (response) => {
          if (requestGenerationRef.current !== generation) {
            // stop() (or a newer start()) already ran before this response
            // arrived. Skip constructing or connecting a room at all,
            // rather than connecting one just to immediately disconnect it.
            return
          }

          const room = new Room()

          room.on(
            RoomEvent.TrackSubscribed,
            (track: Track, publication: RemoteTrackPublication) => {
              if (
                track.kind !== Track.Kind.Audio ||
                // Same stale window as DataReceived below: a track arriving
                // between stop() and its delayed teardown must not attach
                // fresh elements for that teardown to orphan.
                requestGenerationRef.current !== generation
              ) {
                return
              }
              const audioTrack = track as RemoteAudioTrack
              // A remote track delivers no audio to WebAudio unless it is
              // also attached to a media element — but the element itself
              // stays muted; `meter()` below wires the actual (mutable)
              // playback path.
              const element = audioTrack.attach()
              element.style.display = "none"
              element.muted = true
              document.body.appendChild(element)
              elementsRef.current.push(element)
              meter(audioTrack, publication.trackName)
            },
          )

          room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
            if (requestGenerationRef.current !== generation) {
              // stop() has already run but the room stays connected until
              // its fade-out finishes — a session message landing in that
              // window must not flip the just-stopped panel back to an
              // active status.
              return
            }
            try {
              applyMessage(
                JSON.parse(new TextDecoder().decode(payload)) as Envelope,
              )
            } catch {
              // A message this client cannot parse is skipped rather than
              // taking the room down with it.
            }
          })

          room.on(RoomEvent.Disconnected, (reason) => {
            // Also fires for the deliberate disconnect below when this
            // attempt turns out stale — that must not clobber whatever a
            // newer session has already set the panel to. A deliberate
            // stop() bumps the generation before it disconnects the room, so
            // it never reaches the branches below at all.
            if (requestGenerationRef.current !== generation) {
              return
            }
            // The connection is over regardless of why (the worker closing
            // the room after "complete"/"failed", a dropped connection, an
            // expired token) — release the meter loops, audio elements, and
            // AudioContext now rather than leaving them running until the
            // user manually closes a dock that looks finished.
            teardown()
            if (latestStatusRef.current === "complete") {
              // Already reflected as finished by the terminal session
              // message; nothing more to show.
              return
            }
            // Reaching here means LiveKit ended this connection on its own —
            // dropped, closed, or otherwise — without the user asking to
            // stop and without ever hearing the simulation finished. That is
            // a failure the listener should see, not a silent trip back to
            // idle that looks like nothing happened.
            setError(getDisconnectReasonMessage(reason))
            setStatus("error")
          })

          try {
            await room.connect(response.url, response.token)
          } catch (connectError) {
            if (requestGenerationRef.current !== generation) {
              // The user stopped (or restarted) before this connection
              // resolved, so this failure is no longer theirs to see.
              void room.disconnect()
              return
            }
            setError(
              connectError instanceof Error
                ? connectError.message
                : "Could not connect to the live room.",
            )
            setStatus("error")
            return
          }

          if (requestGenerationRef.current !== generation) {
            // stop() (or a newer start()) ran while this was connecting.
            // Retire the room this attempt just stood up rather than
            // reviving a panel the user already stopped.
            void room.disconnect()
            return
          }

          roomRef.current = room
          // The worker joins on its own poll interval, so a listener who gets
          // here first waits rather than failing.
          setStatus((current) =>
            current === "live" || current === "connected" ? current : "waiting",
          )
        },
        onError: (mutationError) => {
          if (requestGenerationRef.current !== generation) {
            return
          }
          setError(getSimulationRoomErrorMessage(mutationError))
          setStatus("error")
        },
      },
    )
  }, [applyMessage, meter, requestRoom, simulationId, status, teardown])

  const stop = useCallback(() => {
    requestGenerationRef.current += 1
    // Ramp gain to 0 first and let the fade finish before tearing the graph
    // down — an instant cut pops exactly like an unramped mute toggle.
    if (fadeGainsForTeardown(audioContextRef.current, gainNodesRef.current)) {
      stopFadeTimeoutRef.current = setTimeout(() => {
        stopFadeTimeoutRef.current = null
        teardown()
      }, ANTI_CLICK_FADE_SECONDS * 1000)
    } else {
      teardown()
    }
    setStatus("idle")
    resetElapsed()
    setLevels({})
  }, [resetElapsed, teardown])

  return {
    status,
    error,
    elapsedSeconds,
    levels,
    // Ordered by first emission: a re-emitted segment (corrected end offset)
    // carries a higher seq than segments that arrived in between, and must
    // not jump past them.
    segments: [...segments.values()].sort((a, b) => a.orderSeq - b.orderSeq),
    events: [...events.values()].sort((a, b) => a.orderSeq - b.orderSeq),
    isMuted,
    toggleMute,
    start,
    stop,
  }
}
